import { useEffect, useRef, useState, type ReactNode } from 'react';

import { partAt, resolvePartPath } from '../../../lib/model-doc.js';
import { keyHint, useCommands, useShortcutContext, useShortcutVersion, type CommandSpec } from '../../../lib/shortcuts/index.js';
import { useModel } from '../../../state/model.js';
import {
  BRUSH_GROUPS,
  gestureBrush,
  SCULPT_BRUSHES,
  sculptOp,
  sculptPartInfo,
  type GestureKind,
  type SculptBrushName,
} from '../../../state/sculpt.js';
import { MenuButton, NumberField, Toggle, type MenuItem } from '../../ui/controls.js';
import { Icon } from '../../ui/icons.js';
import { FalloffCurve } from './FalloffCurve.js';
import '../mesh-edit.css';
import './sculpt.css';

function toHex(c: readonly number[]): string {
  return `#${c.map((v) => Math.round(Math.min(1, Math.max(0, v)) * 255).toString(16).padStart(2, '0')).join('')}`;
}

function fromHex(hex: string): [number, number, number] {
  const n = Number.parseInt(hex.slice(1), 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const brush = (b: SculptBrushName) => () => useModel.getState().setSculptBrush({ brush: b, gesture: null });

/**
 * Sculpt keymap (Blender), context `model.sculpt`. Ctrl+R (voxel remesh)
 * belongs to the remesh tools.
 */
function sculptCommands(): CommandSpec[] {
  const cmd = (id: string, label: string, keys: string[], run: () => void, extra: Partial<CommandSpec> = {}): CommandSpec => ({
    id: `model.sculpt.${id}`,
    label,
    category: 'Model · Sculpt',
    context: 'model.sculpt',
    keys,
    run,
    ...extra,
  });
  const s = () => useModel.getState();
  const resize = (k: number) => () => s().setSculptBrush({ radius: Math.min(600, Math.max(3, s().sculpt.brush.radius * k)) });
  return [
    cmd('brush.draw', 'Draw brush', ['X'], brush('draw')),
    cmd('brush.clay-strips', 'Clay Strips brush', ['C'], brush('clayStrips')),
    cmd('brush.crease', 'Crease brush', ['Shift+C'], brush('crease')),
    cmd('brush.layer', 'Layer brush', ['L'], brush('layer')),
    cmd('brush.inflate', 'Inflate brush', ['I'], brush('inflate')),
    cmd('brush.smooth', 'Smooth brush', ['S'], brush('smooth')),
    cmd('brush.flatten', 'Flatten brush', ['Shift+T'], brush('flatten')),
    cmd('brush.pinch', 'Pinch brush', ['P'], brush('pinch')),
    cmd('brush.grab', 'Grab brush', ['G'], brush('grab')),
    cmd('brush.snake-hook', 'Snake Hook brush', ['K'], brush('snakeHook')),
    cmd('brush.mask', 'Mask brush', ['M'], brush('mask')),
    cmd('mask.invert', 'Invert mask', ['Ctrl+I'], () => sculptOp('sculptMaskInvert')),
    cmd('mask.clear', 'Clear mask', ['Alt+M'], () => sculptOp('sculptMaskClear')),
    cmd('hide.face-set', 'Hide face set under cursor', ['H'], () => sculptOp('sculptHideFaceSet')),
    cmd('hide.isolate', 'Show only face set under cursor', ['Shift+H'], () => sculptOp('sculptIsolateFaceSet')),
    cmd('hide.show-all', 'Show all', ['Alt+H'], () => sculptOp('sculptShowAll')),
    cmd('radius', 'Adjust brush radius', ['F'], () => sculptOp('sculptRadial', { kind: 'radius' })),
    cmd('strength', 'Adjust brush strength', ['Shift+F'], () => sculptOp('sculptRadial', { kind: 'strength' })),
    cmd('radius.smaller', 'Smaller brush', ['['], resize(1 / 1.15), { repeat: true }),
    cmd('radius.bigger', 'Bigger brush', [']'], resize(1.15), { repeat: true }),
    // In the viewport Esc cancels a gesture / radial first (the engine handles it there).
    cmd('exit', 'Leave sculpt mode', ['Escape'], () => s().setSculpt(false), {
      enabled: () => (document.activeElement as HTMLElement | null)?.tagName !== 'CANVAS',
    }),
  ];
}

/** A toolbar button opening a small panel below itself. */
function Popover({ label, title, active, children }: { label: ReactNode; title: string; active?: boolean; children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener('mousedown', onDown, true);
    return () => window.removeEventListener('mousedown', onDown, true);
  }, [open]);
  return (
    <div className="sculpt-pop" ref={ref}>
      <button type="button" className={`tb-btn${active ? ' active' : ''}`} title={title} aria-expanded={open} onClick={() => setOpen(!open)}>
        {label} <Icon name="chevronDown" size={11} />
      </button>
      {open && <div className="sculpt-pop-body">{children}</div>}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="sculpt-row">
      <span>{label}</span>
      {children}
    </label>
  );
}

function maskItems(hint: (id: string) => string): MenuItem[] {
  return [
    { id: 'inv', label: 'Invert mask', shortcut: hint('mask.invert'), run: () => sculptOp('sculptMaskInvert') },
    { id: 'clear', label: 'Clear mask', shortcut: hint('mask.clear'), run: () => sculptOp('sculptMaskClear') },
    { id: 'fill', label: 'Fill mask', run: () => sculptOp('sculptMaskFill') },
    { id: 's1', label: '', separator: true },
    { id: 'grow', label: 'Grow mask', run: () => sculptOp('sculptMaskGrow') },
    { id: 'shrink', label: 'Shrink mask', run: () => sculptOp('sculptMaskShrink') },
    { id: 'smooth', label: 'Smooth mask', run: () => sculptOp('sculptMaskSmooth', { iterations: 2 }) },
    { id: 's2', label: '', separator: true },
    { id: 'hide', label: 'Hide masked', run: () => sculptOp('sculptHideMasked') },
    { id: 'fs', label: 'Face set from mask', run: () => sculptOp('sculptFaceSetFromMask') },
  ];
}

function faceSetItems(hint: (id: string) => string): MenuItem[] {
  return [
    { id: 'h1', label: 'Create', heading: true },
    { id: 'mask', label: 'From mask', run: () => sculptOp('sculptFaceSetFromMask') },
    { id: 'vis', label: 'From visible', run: () => sculptOp('sculptFaceSetFromVisible') },
    { id: 'loose', label: 'From loose parts', run: () => sculptOp('sculptFaceSetFromLooseParts') },
    { id: 'clear', label: 'Clear face sets', run: () => sculptOp('sculptFaceSetClear') },
    { id: 'h2', label: 'Visibility', heading: true },
    { id: 'hide', label: 'Hide face set under cursor', shortcut: hint('hide.face-set'), run: () => sculptOp('sculptHideFaceSet') },
    { id: 'iso', label: 'Show only face set under cursor', shortcut: hint('hide.isolate'), run: () => sculptOp('sculptIsolateFaceSet') },
    { id: 'inv', label: 'Invert visible', run: () => sculptOp('sculptHideInvert') },
    { id: 'all', label: 'Show all', shortcut: hint('hide.show-all'), run: () => sculptOp('sculptShowAll') },
  ];
}

/** Second toolbar row while sculpting: brushes, settings, mask / face sets, detail. */
export function SculptToolbar() {
  const active = useModel((s) => s.sculpt.active);
  const b = useModel((s) => s.sculpt.brush);
  const path = useModel((s) => s.sculpt.path);
  const part = useModel((s) => {
    const index = s.doc && path ? resolvePartPath(s.doc, path) : null;
    return s.doc && index ? partAt(s.doc, index) : null;
  });
  useShortcutContext('model.sculpt', active);
  useCommands(active ? sculptCommands() : [], [active]);
  const keyOf = useKeyHintMap();
  if (!active) return null;
  const s = useModel.getState();
  const info = sculptPartInfo(part);
  const showGesture = gestureBrush(b.brush);
  const brushKey: Partial<Record<SculptBrushName, string>> = {
    draw: keyOf('brush.draw'),
    clayStrips: keyOf('brush.clay-strips'),
    crease: keyOf('brush.crease'),
    layer: keyOf('brush.layer'),
    inflate: keyOf('brush.inflate'),
    smooth: keyOf('brush.smooth'),
    flatten: keyOf('brush.flatten'),
    pinch: keyOf('brush.pinch'),
    grab: keyOf('brush.grab'),
    snakeHook: keyOf('brush.snake-hook'),
    mask: keyOf('brush.mask'),
  };
  const setGesture = (gesture: GestureKind | null) => s.setSculptBrush({ gesture });
  const detailOn = info.detail !== 0;
  return (
    <div className="viewport-toolbar mesh-toolbar sculpt-toolbar" role="toolbar" aria-label="Sculpt">
      {BRUSH_GROUPS.map((group) => (
        <div key={group} className="tb-group segmented sculpt-palette" role="group" aria-label={group}>
          {SCULPT_BRUSHES.filter((x) => x.group === group).map((x) => {
            const key = brushKey[x.brush];
            return (
              <button
                key={x.brush}
                type="button"
                className={`tb-btn sculpt-brush${b.brush === x.brush ? ' active' : ''}`}
                title={`${x.label}: ${x.title}${key ? ` (${key})` : ''}`}
                aria-pressed={b.brush === x.brush}
                onClick={() => s.setSculptBrush({ brush: x.brush, gesture: gestureBrush(x.brush) && x.brush === 'hide' ? b.gesture ?? 'box' : null })}
              >
                <span className="sculpt-glyph">{x.glyph}</span>
                <span className="sculpt-name">{x.label}</span>
              </button>
            );
          })}
        </div>
      ))}
      {showGesture && (
        <div className="tb-group segmented" role="group" aria-label="Gesture">
          {b.brush !== 'hide' && (
            <button type="button" className={`tb-btn${b.gesture === null ? ' active' : ''}`} title="Paint with the brush" onClick={() => setGesture(null)}>
              <Icon name="pencil" size={13} />
            </button>
          )}
          <button type="button" className={`tb-btn${b.gesture === 'lasso' ? ' active' : ''}`} title="Lasso: drag an outline" onClick={() => setGesture('lasso')}>
            <Icon name="marquee" size={13} /> Lasso
          </button>
          <button type="button" className={`tb-btn${b.gesture === 'box' ? ' active' : ''}`} title="Box: drag a rectangle" onClick={() => setGesture('box')}>
            <Icon name="square" size={13} /> Box
          </button>
          <button
            type="button"
            className={`tb-btn${b.gesture === 'line' ? ' active' : ''}`}
            title="Line: everything to the right of the dragged line, seen in its drawing direction (Ctrl: unmask / show)"
            onClick={() => setGesture('line')}
          >
            <Icon name="line" size={13} /> Line
          </button>
        </div>
      )}
      <div className="tb-group sculpt-sliders">
        <label className="tb-btn sculpt-slider" title={`Brush radius in screen pixels (${keyOf('radius')} to drag, [ and ])`}>
          Radius
          <input type="range" min={3} max={300} value={b.radius} onChange={(e) => s.setSculptBrush({ radius: Number(e.target.value) })} />
          <span className="sculpt-value">{Math.round(b.radius)}</span>
        </label>
        <label className="tb-btn sculpt-slider" title={`Brush strength (${keyOf('strength')} to drag)`}>
          Strength
          <input type="range" min={0.01} max={1} step={0.01} value={b.strength} onChange={(e) => s.setSculptBrush({ strength: Number(e.target.value) })} />
          <span className="sculpt-value">{b.strength.toFixed(2)}</span>
        </label>
        {b.brush === 'paint' && (
          <input type="color" title="Paint color" value={toHex(b.color)} onChange={(e) => s.setSculptBrush({ color: fromHex(e.target.value) })} />
        )}
      </div>
      <div className="tb-group">
        <Popover label="Falloff" title="Brush falloff curve" active={b.falloff !== null}>
          <FalloffCurve value={b.falloff} onChange={(falloff) => s.setSculptBrush({ falloff })} />
        </Popover>
        <Popover label="Stroke" title="Stroke spacing, smooth stroke, front faces" active={b.lazy > 0 || b.frontFaces}>
          <Row label="Spacing %">
            <NumberField value={b.spacing} min={2} max={400} step={1} integer onChange={(spacing) => s.setSculptBrush({ spacing })} />
          </Row>
          <Row label="Smooth stroke px">
            <NumberField value={b.lazy} min={0} max={300} step={1} integer title="Trail the cursor by this radius (0 = off)" onChange={(lazy) => s.setSculptBrush({ lazy })} />
          </Row>
          <Row label="Smooth factor">
            <NumberField value={b.lazyFactor} min={0.05} max={1} step={0.05} onChange={(lazyFactor) => s.setSculptBrush({ lazyFactor })} />
          </Row>
          <Row label="Front faces only">
            <Toggle checked={b.frontFaces} onChange={(frontFaces) => s.setSculptBrush({ frontFaces })} />
          </Row>
          <Row label="Normal radius">
            <NumberField value={b.normalRadius} min={0.05} max={4} step={0.05} title="Clay strips: plane sampling radius (× brush radius)" onChange={(normalRadius) => s.setSculptBrush({ normalRadius })} />
          </Row>
          <Row label="Layer: persistent">
            <Toggle checked={b.persistent} onChange={(persistent) => s.setSculptBrush({ persistent })} />
          </Row>
        </Popover>
      </div>
      <div className="tb-group segmented" role="group" aria-label="Symmetry">
        <span className="mesh-label">
          <Icon name="mirror" size={13} />
        </span>
        {(
          [
            ['mirror', 'X'],
            ['mirrorY', 'Y'],
            ['mirrorZ', 'Z'],
          ] as const
        ).map(([k, label]) => (
          <button
            key={k}
            type="button"
            className={`tb-btn${b[k] ? ' active' : ''}`}
            title={`Mirror strokes across ${label} (a sculpt's own symmetry axis always mirrors)`}
            aria-pressed={b[k]}
            onClick={() => s.setSculptBrush({ [k]: !b[k] })}
          >
            {label}
          </button>
        ))}
      </div>
      <div className="tb-group">
        <MenuButton className="tb-btn" title="Mask" items={() => maskItems(keyOf)}>
          Mask <Icon name="chevronDown" size={11} />
        </MenuButton>
        <MenuButton className="tb-btn" title="Face sets and visibility" items={() => faceSetItems(keyOf)}>
          Face Sets <Icon name="chevronDown" size={11} />
        </MenuButton>
      </div>
      {info.kind !== 'none' && (
        <div className="tb-group">
          <Popover label={detailOn ? 'Dyntopo ●' : 'Dyntopo'} title="Dynamic topology: add detail where you sculpt" active={detailOn}>
            <Row label="Dynamic topology">
              <Toggle checked={detailOn} onChange={(on) => sculptOp('sculptSetDetail', { detail: on ? -0.3 : 0 })} />
            </Row>
            <Row label={info.detail < 0 ? 'Detail (× radius)' : 'Detail size (m)'}>
              <NumberField
                value={Math.abs(info.detail)}
                min={0.0005}
                step={info.detail < 0 ? 0.05 : 0.001}
                title="Target edge length"
                onChange={(v) => sculptOp('sculptSetDetail', { detail: info.detail < 0 ? -v : v })}
              />
            </Row>
            <Row label="Relative to brush">
              <Toggle
                checked={info.detail < 0}
                onChange={(rel) => sculptOp('sculptSetDetail', { detail: rel ? -0.3 : 0.01 })}
              />
            </Row>
            <div className="sculpt-row">
              <button
                type="button"
                className="tb-btn"
                title="Remesh the whole surface to the detail size (relative detail: 1 cm)"
                onClick={() => sculptOp('sculptDetailFlood', info.detail > 0 ? { detail: info.detail } : { detail: 0.01 })}
              >
                Detail flood fill
              </button>
            </div>
          </Popover>
          {info.kind === 'op' && (
            <Popover label={info.multires ? `Multires ${info.multires.levels}` : 'Multires'} title="Multiresolution subdivision" active={info.multires !== null}>
              <div className="sculpt-row">
                <button type="button" className="tb-btn" title="Add a Catmull-Clark level and sculpt on it" onClick={() => sculptOp('multiresSubdivide')}>
                  Subdivide
                </button>
                <button type="button" className="tb-btn" disabled={!info.multires} title="Remove levels above the sculpt level" onClick={() => sculptOp('multiresDeleteHigher')}>
                  Delete higher
                </button>
              </div>
              {info.multires && (
                <>
                  <Row label="Levels">
                    <NumberField value={info.multires.levels} min={0} max={6} step={1} integer onChange={(levels) => sculptOp('multiresSetLevels', { levels })} />
                  </Row>
                  <Row label="Sculpt level">
                    <NumberField
                      value={info.multires.sculptLevel ?? info.multires.levels}
                      min={0}
                      max={info.multires.levels}
                      step={1}
                      integer
                      onChange={(sculptLevel) => sculptOp('multiresSetLevels', { sculptLevel })}
                    />
                  </Row>
                  <Row label="Viewport levels">
                    <NumberField
                      value={info.multires.viewportLevels ?? info.multires.levels}
                      min={0}
                      max={info.multires.levels}
                      step={1}
                      integer
                      onChange={(viewportLevels) => sculptOp('multiresSetLevels', { viewportLevels })}
                    />
                  </Row>
                  <div className="sculpt-row">
                    <button
                      type="button"
                      className="tb-btn"
                      title="Move the base cage to the sculpted shape and clear the strokes (finer detail is lost; Apply in the op stack keeps everything)"
                      onClick={() => sculptOp('multiresApplyBase')}
                    >
                      Apply base
                    </button>
                  </div>
                </>
              )}
            </Popover>
          )}
        </div>
      )}
      <div className="tb-group">
        <button
          type="button"
          className="tb-btn"
          title="Fast preview builds sculpts at half detail while sculpting; Full shows final quality"
          onClick={() => s.setSculptBrush({ preview: b.preview > 1 ? 1 : 2 })}
        >
          {b.preview > 1 ? 'Fast' : 'Full'}
        </button>
        <button type="button" className="tb-btn" title="Leave sculpt mode (Esc)" onClick={() => s.setSculpt(false)}>
          <Icon name="check" size={13} /> Done
        </button>
      </div>
      <span className="muted mesh-label">Drag on the part · Alt / empty space orbits · Shift smooth · Ctrl invert</span>
    </div>
  );
}

/** Key hints of the sculpt commands (re-render when bindings change). */
function useKeyHintMap(): (id: string) => string {
  useShortcutVersion();
  return (id: string) => keyHint(`model.sculpt.${id}`);
}
